import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  InputBase,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import axios from "axios";

const ApiKeyModal = ({ open, setOpen, hasExistingKey = true, onKeyGenerated }) => {
  const [step, setStep] = useState("confirm"); // confirm → generated
  const [loading, setLoading] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [copied, setCopied] = useState(false);
  const didAutoGenerateRef = useRef(false);

  const generateApiKey = async () => {
    setLoading(true);

    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}generate-api-key`;

      const res = await axios.post(url, {
        organization_id: JSON.parse(localStorage.getItem("organization")).orgId.S,
      });

      setApiKey(res.data.api_key);
      setStep("generated");
      if (onKeyGenerated) {
        onKeyGenerated(res.data.organization);
      }
    } catch (err) {
      // Error surfaced to user via UI state; no need to log
    }

    setLoading(false);
  };

  // When opening without an existing key, generate immediately (no confirmation).
  useEffect(() => {
    if (!open) {
      didAutoGenerateRef.current = false;
      return;
    }
    if (hasExistingKey || didAutoGenerateRef.current) return;
    didAutoGenerateRef.current = true;
    generateApiKey();
  }, [open, hasExistingKey]);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const closeAll = () => {
    setApiKey("");
    setCopied(false);
    setOpen(false);
    setTimeout(() => {
      setStep("confirm");
    }, 2000);
  };

  return (
    <Dialog
      open={open}
      onClose={closeAll}
      fullWidth
      maxWidth="xs"
      BackdropProps={{
        style: { backgroundColor: "rgb(144,150,160,0.8)" },
      }}
    >
      {/* STEP 1 — CONFIRM REGENERATE (only when user already has a key) or GENERATING (first-time) */}
      {step === "confirm" && (
        <div className="p-6">
          {hasExistingKey ? (
            <>
              <div className="flex items-center  space-x-2 mb-2  ">
                <div className="flex justify-center ">
                  <WarningAmberIcon sx={{ color: "#D32F2F", fontSize: 20 }} />
                </div>
                <div className="text-center text-[18px] font-semibold">
                  Regenerate API Key?
                </div>
              </div>
              <p className=" text-[#4B5563]  text-[14px] mb-6  leading-relaxed">
                Regenerating will immediately invalidate the existing API key.
                Any integrations using the old key will stop working until updated.
              </p>
              <div className="flex justify-end space-x-3 pt-3">
                <Button
                  onClick={closeAll}
                  sx={{
                    textTransform: "none",
                    border: "1px solid #D1D5DB",
                    color: "#333",
                    px: 2,
                    py: 1,
                    borderRadius: "8px",
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={generateApiKey}
                  disabled={loading}
                  sx={{
                    textTransform: "none",
                    bgcolor: "#D32F2F",
                    color: "white",
                    px: 1,
                    py: 1,
                    borderRadius: "8px",
                  }}
                >
                  {loading ? (
                    <CircularProgress size="1.2rem" sx={{ color: "white" }} />
                  ) : (
                    "Regenerate"
                  )}
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="text-center text-[18px] font-semibold mb-2">
                Generate API Key
              </div>
              <p className="text-[#4B5563] text-[14px] mb-6 text-center">
                {loading ? "Generating your API key…" : "Preparing…"}
              </p>
              <div className="flex justify-center mb-2">
                {loading && (
                  <CircularProgress size="2rem" sx={{ color: "#605BFF" }} />
                )}
              </div>
              <div className="flex justify-end pt-3">
                <Button
                  onClick={closeAll}
                  sx={{
                    textTransform: "none",
                    border: "1px solid #D1D5DB",
                    color: "#333",
                    px: 2,
                    py: 1,
                    borderRadius: "8px",
                  }}
                >
                  Cancel
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {/* STEP 2 — SHOW GENERATED API KEY */}
      {step === "generated" && (
        <div className="p-6">
          <div className="text-left text-[18px] font-semibold mb-3">
            API Key Generated
          </div>

          <p className="text-left  text-[#4B5563] text-[14px] mb-4">
            Copy and save this key now. You will not be able to view it again.
          </p>

          <div className="border rounded-lg flex items-center bg-[#F8F8F8] px-2 py-2">
            <InputBase
              value={apiKey}
              readOnly
              sx={{ flex: 1, fontSize: "14px", overflow: "hidden", fontWeight:500 }}
            />
            <Tooltip title={copied ? "Copied!" : "Copy"}>
              <IconButton onClick={copyToClipboard}>
                {copied ? (
                  <CheckCircleIcon fontSize="small" sx={{ color: "#16a34a" }} />
                ) : (
                  <ContentCopyIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </div>

          <div className="flex justify-end pt-6">
            <Button
              onClick={closeAll}
              variant="contained"
                color="primary"
                sx={{
                px: 2,
                py: 1,
                borderRadius: "8px",
                fontWeight:520
              }}
            >
              Done
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
};

export default ApiKeyModal;
